/**
 * A conta do Sicredi, do jeito que a tela precisa dela.
 *
 * Porte de `lib/features/financas/data/services/financas_service.dart`. Só
 * leitura. O servidor fala com o Sicredi por mTLS e devolve o que veio de lá
 * quase intacto; aqui só se dá nome aos campos.
 *
 * Não existe neste arquivo — nem em nenhum outro do app — caminho para pagar,
 * transferir ou mexer em dinheiro. A integração foi feita com as duas APIs de
 * consulta e nada mais, de propósito.
 */

import { idioma, locale } from '../i18n/idioma';

export interface Saldo {
  atual: number;
  contaCorrente: number;
  aplicacaoAutomatica: number;
  bloqueioJudicial: number;
  retido: number;
  limiteDisponivel: number;
}

export interface Movimento {
  id: string;
  data: Date | null;
  descricao: string;
  complemento: string;
  documento: string;
  /** Negativo é débito, como o Sicredi manda. */
  valor: number;
  saldo: number;
}

export interface Extrato {
  saldoAnterior: number;
  movimentos: Movimento[];
  pagina: number;
  paginas: number;
}

/**
 * Quantos movimentos por página. O Sicredi pagina, e o teto do servidor é 200;
 * cem cobre um mês inteiro de conta doméstica numa chamada só.
 */
export const POR_PAGINA = 100;

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const texto = (v: unknown): string => `${v ?? ''}`.trim();

export function saldoDeJson(cru: unknown): Saldo {
  const j = (cru ?? {}) as Record<string, unknown>;
  return {
    atual: num(j.saldoAtual),
    contaCorrente: num(j.saldoContaCorrente),
    aplicacaoAutomatica: num(j.saldoAplicAutomatica),
    bloqueioJudicial: num(j.saldoBloqueioJudicial),
    retido: num(j.saldoRetido),
    limiteDisponivel: num(j.limiteChequeEspecialDisponivel),
  };
}

export function movimentoDeJson(cru: unknown): Movimento {
  const j = (cru ?? {}) as Record<string, unknown>;
  const quando = new Date(texto(j.data));
  return {
    id: texto(j.idMovimento),
    data: Number.isNaN(quando.getTime()) ? null : quando,
    descricao: texto(j.descricao),
    complemento: texto(j.complemento),
    documento: texto(j.documento),
    valor: num(j.valor),
    saldo: num(j.saldo),
  };
}

export function extratoDeJson(cru: unknown): Extrato {
  const j = (cru ?? {}) as Record<string, unknown>;
  const lista = Array.isArray(j.dtlMovimentos) ? j.dtlMovimentos : [];
  return {
    saldoAnterior: num(j.saldoAnterior),
    movimentos: lista
      .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
      .map(movimentoDeJson),
    pagina: Math.trunc(num(j.pageNumber)),
    paginas: Math.trunc(num(j.totalPages)) || 1,
  };
}

/** O dia como o Sicredi pede, sem hora e sem fuso. */
export function diaIso(d: Date): string {
  const p = (n: number, casas = 2) => String(n).padStart(casas, '0');
  return `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** O primeiro dia do mês de uma data. */
export const inicioDoMes = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * O último dia do mês. Dia zero do mês seguinte, o que já acerta fevereiro e
 * os anos bissextos sem tabela nenhuma — é a mesma conta do Flutter.
 */
export const fimDoMes = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** `setembro de 2026`. Em inglês o `Intl` já monta o "September 2026" sozinho. */
export const nomeDoMes = (d: Date): string =>
  idioma() === 'pt'
    ? `${MESES[d.getMonth()]} de ${d.getFullYear()}`
    : new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(d);

/**
 * O dinheiro é real, e continua real com o app em inglês: a conta é de um banco
 * brasileiro. O que muda com a língua é só como o número se escreve --
 * `R$ 1.234,56` para quem lê português, `R$ 1,234.56` para quem lê inglês.
 */
const REAIS = new Map<string, Intl.NumberFormat>();

export const emReais = (valor: number): string => {
  const l = locale();
  let f = REAIS.get(l);
  if (!f) REAIS.set(l, (f = new Intl.NumberFormat(l, { style: 'currency', currency: 'BRL' })));
  return f.format(valor);
};

/** `dd/MM`, que é o `DateFormat('dd/MM')` da tela de lá. */
export function diaCurto(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}
